import { User } from '@/types/user';
import { UserWire } from '../schemas/user';

/**
 * Mapping functions to convert wire format (backend) to domain format (app)
 */

/**
 * Convert wire user format to domain user format
 * Handles type conversions: 0/1 -> boolean, ISO strings -> Unix timestamps
 */
export function mapUserWireToDomain(wire: UserWire): User {
  // Convert ISO datetime strings to Unix timestamps (milliseconds)
  const createdAt = wire.created_at ? new Date(wire.created_at).getTime() : Date.now();
  const updatedAt = wire.updated_at ? new Date(wire.updated_at).getTime() : Date.now();

  return {
    id: wire.id,
    name: wire.name || '', // Handle null/undefined
    email: wire.email || undefined, // Optional for public user responses
    emailVerified: wire.email_verified === 1, // Convert 0/1 to boolean
    username: wire.username || undefined, // Convert null to undefined
    displayUsername: wire.display_username || undefined,
    image: wire.image || undefined, // Convert null to undefined
    role: wire.role,
    banned: wire.banned === 1, // Convert 0/1 to boolean
    // Source isDemo from account_type; fall back to the legacy is_demo flag during transition
    isDemo: wire.account_type ? wire.account_type === 'demo' : wire.is_demo === 1,
    createdAt,
    updatedAt,
    // Legacy fields
    bio: wire.bio || undefined,
    avatar: wire.avatar || undefined,
    followers: wire.followers,
    following: wire.following,
    checkins: wire.checkins,
  };
}

/**
 * Convert domain user format to wire format (for requests)
 */
export function mapUserDomainToWire(domain: Partial<User>): Partial<UserWire> {
  const wire: Partial<UserWire> = {};

  if (domain.id !== undefined) wire.id = domain.id;
  if (domain.name !== undefined) wire.name = domain.name;
  if (domain.email !== undefined) wire.email = domain.email;
  if (domain.emailVerified !== undefined) wire.email_verified = domain.emailVerified ? 1 : 0;
  if (domain.username !== undefined) wire.username = domain.username;
  if (domain.displayUsername !== undefined) wire.display_username = domain.displayUsername;
  if (domain.image !== undefined) wire.image = domain.image;
  if (domain.role !== undefined) wire.role = domain.role;
  if (domain.banned !== undefined) wire.banned = domain.banned ? 1 : 0;
  if (domain.isDemo !== undefined) wire.is_demo = domain.isDemo ? 1 : 0;
  if (domain.createdAt !== undefined) wire.created_at = new Date(domain.createdAt).toISOString();
  if (domain.updatedAt !== undefined) wire.updated_at = new Date(domain.updatedAt).toISOString();
  // Legacy fields
  if (domain.bio !== undefined) wire.bio = domain.bio;
  if (domain.avatar !== undefined) wire.avatar = domain.avatar;
  if (domain.followers !== undefined) wire.followers = domain.followers;
  if (domain.following !== undefined) wire.following = domain.following;
  if (domain.checkins !== undefined) wire.checkins = domain.checkins;

  return wire;
}
